import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { PackagePlus, Pencil, Send, AlertCircle, CheckCircle2, Clock } from "lucide-react";

const BLANK = {
  name: "", brand: "", category: "", product_category: "",
  model: "", other_model: "", product_size: "",
  specifications: "", remarks: "",
  comprehensive_months: "", compressor_months: "", motor_months: "",
  parts_months: "", service_months: "", warranty_clause: "",
};

/**
 * CCR-facing page: submit a new product for Service Head approval and track
 * the status of one's own submissions. Admins/SH can use the regular
 * Settings → Products page to do everything else.
 */
export default function ProductRequest() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);  // product currently being edited

  const load = async () => {
    setLoading(true);
    try {
      const [m, b] = await Promise.all([
        api.get("/products", { params: { status: "mine" } }),
        api.get("/masters/brands"),
      ]);
      setMine(m.data);
      setBrands(b.data);
    } catch (e) {
      toast.error("Failed to load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="product-request-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Add New Product</h1>
          <p className="text-sm text-slate-500 mt-1">
            Add a new product. CCR can now add products directly — they go live immediately for complaint registration. Make sure to fill warranty details accurately so warranty rules update correctly.
          </p>
        </div>
        <Button className="bg-slate-900 text-white" onClick={() => { setEditing(null); setOpenCreate(true); }}
                data-testid="new-product-request-btn">
          <PackagePlus size={14} className="mr-1" /> Add Product
        </Button>
      </div>

      <Card data-testid="my-product-requests">
        <CardContent className="p-0">
          <div className="px-3 py-2 border-b text-xs text-slate-500">
            My submissions ({mine.length}) {loading && "· loading…"}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <Th>Status</Th><Th>Brand</Th><Th>Product</Th><Th>Model</Th><Th>Category</Th><Th>Size</Th><Th>SH remarks</Th><Th align="right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {mine.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-slate-400">You have not submitted any products yet.</td></tr>}
                {mine.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`}>
                    <td><StatusPill status={p.status} /></td>
                    <td>{p.brand || "—"}</td>
                    <td className="font-medium">{p.name}</td>
                    <td className="font-mono text-xs">{p.model || "—"}</td>
                    <td>{p.product_category || p.category || "—"}</td>
                    <td className="text-xs">{p.product_size || "—"}</td>
                    <td className="text-xs text-amber-700 max-w-xs truncate" title={p.review_remarks || ""}>{p.review_remarks || "—"}</td>
                    <td className="text-right">
                      {(p.status === "pending" || p.status === "needs_correction") && (
                        <Button size="sm" variant="outline"
                                onClick={() => { setEditing(p); setOpenCreate(true); }}
                                data-testid={`edit-product-${p.id}`}>
                          <Pencil size={12} className="mr-1" />
                          {p.status === "needs_correction" ? "Correct & Resubmit" : "Edit"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <ProductRequestDialog open={openCreate} setOpen={setOpenCreate}
                             editing={editing} brands={brands}
                             onDone={load} />
    </div>
  );
}

function ProductRequestDialog({ open, setOpen, editing, brands, onDone }) {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(editing ? { ...BLANK, ...editing } : BLANK);
  }, [open, editing]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Product Name is required"); return; }
    if (!form.brand?.trim()) { toast.error("Brand is required"); return; }
    if (!form.model?.trim()) { toast.error("Model Number is required"); return; }
    if (!form.product_category?.trim()) { toast.error("Product Category is required"); return; }
    if (!form.product_size?.trim()) { toast.error("Product Size is required"); return; }
    if (!form.warranty_clause?.trim()) { toast.error("Warranty Clause is required"); return; }
    const months = ["comprehensive_months", "compressor_months", "motor_months", "parts_months", "service_months"];
    const hasCoverage = months.some((k) => Number(form[k] || 0) > 0);
    if (!hasCoverage) {
      toast.error("At least one warranty period is required", { description: "Fill Comprehensive / Compressor / Motor / Parts / Service months." });
      return;
    }
    const payload = { ...form };
    months.forEach((k) => {
      payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]);
    });
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added — now available for complaint registration");
      }
      setOpen(false); onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const isEdit = !!editing;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-request-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Product — ${editing.name}` : "Add New Product"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the product details. Changes save immediately."
              : "Add a new product. It goes live for complaint registration as soon as you save. Warranty fields below also auto-update the matching warranty rule."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Brand *">
            <Select value={form.brand} onValueChange={(v) => update("brand", v)}>
              <SelectTrigger data-testid="pr-brand"><SelectValue placeholder="Pick brand" /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <Fld label="Product Category *">
            <Input placeholder="e.g. AC / TV / Refrigerator / Washing Machine"
                   value={form.product_category} onChange={(e) => update("product_category", e.target.value)}
                   data-testid="pr-category" />
          </Fld>
          <Fld label="Product Name / Type *"><Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="pr-name" /></Fld>
          <Fld label="Model Number *"><Input value={form.model} onChange={(e) => update("model", e.target.value)} data-testid="pr-model" /></Fld>
          <Fld label="Other Model"><Input value={form.other_model || ""} onChange={(e) => update("other_model", e.target.value)} data-testid="pr-other-model" /></Fld>
          <Fld label="Product Size *"><Input placeholder="e.g. 55 inch / 1.5 TON / 250 L" value={form.product_size || ""} onChange={(e) => update("product_size", e.target.value)} data-testid="pr-size" /></Fld>
          <Fld label="Specifications" full><Textarea rows={2} value={form.specifications || ""} onChange={(e) => update("specifications", e.target.value)} data-testid="pr-specs" /></Fld>
          <div className="col-span-2 border-t border-slate-200 pt-3 mt-2 text-xs uppercase tracking-wide text-slate-500 font-semibold">Warranty</div>
          <Fld label="Comprehensive (months)"><Input type="number" min="0" value={form.comprehensive_months ?? ""} onChange={(e) => update("comprehensive_months", e.target.value)} data-testid="pr-comp-months" /></Fld>
          <Fld label="Compressor (months)"><Input type="number" min="0" value={form.compressor_months ?? ""} onChange={(e) => update("compressor_months", e.target.value)} data-testid="pr-compressor-months" /></Fld>
          <Fld label="Motor (months)"><Input type="number" min="0" value={form.motor_months ?? ""} onChange={(e) => update("motor_months", e.target.value)} data-testid="pr-motor-months" /></Fld>
          <Fld label="Parts (months)"><Input type="number" min="0" value={form.parts_months ?? ""} onChange={(e) => update("parts_months", e.target.value)} data-testid="pr-parts-months" /></Fld>
          <Fld label="Service (months)"><Input type="number" min="0" value={form.service_months ?? ""} onChange={(e) => update("service_months", e.target.value)} data-testid="pr-service-months" /></Fld>
          <Fld label="Warranty Clause *" full><Textarea rows={2} value={form.warranty_clause || ""} onChange={(e) => update("warranty_clause", e.target.value)} placeholder="e.g. 1 year comprehensive + 5 year compressor as per HISENSE policy" data-testid="pr-clause" /></Fld>
          <Fld label="Image URL"><Input value={form.image_url || ""} onChange={(e) => update("image_url", e.target.value)} data-testid="pr-image-url" /></Fld>
          <Fld label="Manual / Document URL"><Input value={form.document_url || ""} onChange={(e) => update("document_url", e.target.value)} data-testid="pr-doc-url" /></Fld>
          <Fld label="Internal Remarks" full><Textarea rows={2} value={form.remarks || ""} onChange={(e) => update("remarks", e.target.value)} data-testid="pr-remarks" /></Fld>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-900 text-white" disabled={busy} data-testid="pr-submit">
            <Send size={13} className="mr-1" /> {busy ? "Saving…" : (isEdit ? "Save changes" : "Add Product")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function StatusPill({ status }) {
  const map = {
    pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock, label: "Pending" },
    needs_correction: { cls: "bg-orange-50 text-orange-700 border-orange-200", Icon: AlertCircle, label: "Needs Correction" },
    approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2, label: "Approved" },
    rejected: { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertCircle, label: "Rejected" },
  };
  const s = map[status] || { cls: "bg-slate-50 text-slate-700 border-slate-200", Icon: CheckCircle2, label: status || "—" };
  const { Icon } = s;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${s.cls}`} data-testid={`status-pill-${status}`}><Icon size={11} />{s.label}</span>;
}
function Fld({ label, children, full }) {
  return <div className={full ? "col-span-2" : ""}><Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>{children}</div>;
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
